import { alpha, Box, useMediaQuery, useTheme } from "@mui/material";
import { KeyboardEvent, MouseEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

interface WaveformProps {
  peaks: number[];
  height?: number;
  barColor?: (index: number) => string | null;
  progress?: number | null;
  onSeek?: (fraction: number) => void;
  reveal?: boolean;
  sweep?: boolean;
}

const REVEAL_MS = 700;
const KEY_STEP = 0.02;
const SCAN_MASK =
  "linear-gradient(90deg, transparent 0%, black 50%, transparent 100%)";

/*
 * The recording's shape as a row of bars.
 * The result view colors it by speaker and uses it to seek.
 *
 * NOTE: Does not support every format the backend accepts.
 *       The peaks come from the browser's `decodeAudioData` (see `useWaveform`), which does not decode well for some formats.
 *       For those files, this card shows no waveform, and a length only when the WAV has a `fact` chunk.
 *       A robust fix would be to compute it in the backend (`POST /api/audio`), store it in the task, and return it with the result.
 */
export const Waveform = ({
  peaks,
  height = 56,
  barColor,
  progress = null,
  onSeek,
  reveal = false,
  sweep = false,
}: WaveformProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const reducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const [painted, setPainted] = useState(!reveal);

  useEffect(() => {
    if (painted) return;
    const frame = requestAnimationFrame(() => setPainted(true));
    return () => cancelAnimationFrame(frame);
  }, [painted]);

  const silence = alpha(theme.palette.text.secondary, 0.25);
  const defaultColor = theme.palette.primary.main;
  const scanning = sweep && !reducedMotion;

  const bars = (lit: boolean) =>
    peaks.map((peak, index) => {
      const color = (barColor ? barColor(index) : defaultColor) ?? silence;
      const played =
        progress === null || progress === 0
          ? true
          : index / peaks.length <= progress;
      const opacity = played && !scanning ? 0.85 : 0.35;
      const resting = painted ? alpha(color, opacity) : silence;
      return (
        <Box
          key={index}
          sx={{
            flex: 1,
            minWidth: 0,
            height: `${Math.max(4, peak * 100)}%`,
            borderRadius: "2px",
            bgcolor: lit ? color : resting,
            transition: reducedMotion
              ? "none"
              : "background-color 300ms ease-out",
            transitionDelay:
              painted && reveal && !reducedMotion
                ? `${(index / peaks.length) * REVEAL_MS}ms`
                : "0ms",
          }}
        />
      );
    });

  const seekAt = (event: MouseEvent<HTMLDivElement>) => {
    if (!onSeek) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const fraction = (event.clientX - rect.left) / rect.width;
    onSeek(Math.max(0, Math.min(1, fraction)));
  };

  const seekByKey = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!onSeek || progress === null) return;
    if (event.key === "ArrowRight" || event.key === "ArrowUp") {
      event.preventDefault();
      onSeek(Math.min(1, progress + KEY_STEP));
    } else if (event.key === "ArrowLeft" || event.key === "ArrowDown") {
      event.preventDefault();
      onSeek(Math.max(0, progress - KEY_STEP));
    } else if (event.key === "Home") {
      event.preventDefault();
      onSeek(0);
    } else if (event.key === "End") {
      event.preventDefault();
      onSeek(1);
    }
  };

  return (
    <Box
      role={onSeek ? "slider" : undefined}
      aria-label={onSeek ? t("audio.player.position") : undefined}
      aria-valuemin={onSeek ? 0 : undefined}
      aria-valuemax={onSeek ? 100 : undefined}
      aria-valuenow={onSeek ? Math.round((progress ?? 0) * 100) : undefined}
      tabIndex={onSeek ? 0 : undefined}
      onClick={onSeek ? seekAt : undefined}
      onKeyDown={onSeek ? seekByKey : undefined}
      sx={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        gap: "2px",
        height,
        width: "100%",
        overflow: "hidden",
        cursor: onSeek ? "pointer" : "default",
        borderRadius: "6px",
        "&:focus-visible": {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: 2,
        },
      }}
    >
      {bars(false)}

      {progress !== null && (
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            top: 0,
            bottom: 0,
            left: `${progress * 100}%`,
            width: 2,
            ml: "-1px",
            borderRadius: 1,
            bgcolor: theme.palette.primary.main,
            pointerEvents: "none",
          }}
        />
      )}

      {scanning && (
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            gap: "2px",
            pointerEvents: "none",
            maskImage: SCAN_MASK,
            WebkitMaskImage: SCAN_MASK,
            maskSize: "35% 100%",
            WebkitMaskSize: "35% 100%",
            maskRepeat: "no-repeat",
            WebkitMaskRepeat: "no-repeat",
            animation: "audio-scan 2.2s linear infinite",
            "@keyframes audio-scan": {
              from: { maskPosition: "-60% 0", WebkitMaskPosition: "-60% 0" },
              to: { maskPosition: "160% 0", WebkitMaskPosition: "160% 0" },
            },
          }}
        >
          {bars(true)}
        </Box>
      )}
    </Box>
  );
};
